import React, { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    IoArrowBackCircle,
    IoDocumentTextOutline,
    IoCalendarOutline,
    IoCheckmarkCircle,
    IoCloseCircle,
    IoTimeOutline,
    IoChevronDownOutline,
    IoChevronForwardOutline,
    IoInformationCircle,
    IoShare,
    IoLayersOutline,
    IoCashOutline,
    IoAlbumsOutline,
    IoChatbubbleEllipsesOutline,
    IoInformationCircleOutline,
    IoLogoWhatsapp,
    IoSendOutline,
    IoStatsChartOutline,
    IoTrendingDownOutline,
    IoTrendingUpOutline,
    IoInformationOutline
} from "react-icons/io5";
import { Loading } from "../../shared/loading/Loading";
import { useAuth } from "../../../contexts/AuthContext";
import { useResponderProposta, useRespostasFornecedor, usePropostasComprador, usePropostasFornecedor } from "../../../hooks/usePropostasFornecedor";
import { useFormRespostaPropostaStore } from "../../../stores/formRespostaPropostaStore";
import Alert from "../../shared/alert/Alert";
import Money from "../../../utils/money";
import Masks from "../../../utils/masks";
import Validacoes from "../../../utils/validacoes";
import Style from "./index.module.css";

const STATUS_MAP = {
    aprovada: { label: "Aprovada", color: "#28a745", icon: <IoCheckmarkCircle /> },
    reprovada: { label: "Reprovada", color: "#dc3545", icon: <IoCloseCircle /> },
    pendente: { label: "Pendente", color: "#ffc107", icon: <IoTimeOutline /> },
    analise: { label: "Em Análise", color: "#17a2b8", icon: <IoInformationCircle /> },
    respondido: { label: "Respondido", color: "#007bff", icon: <IoCheckmarkCircle /> },
};

const tiposPagamento = [
    { valor: "00", label: "Boleto/DDA" },
    { valor: "08", label: "TED - Mesma titularidade" },
    { valor: "11", label: "Dinheiro" },
    { valor: "14", label: "PIX" },
];

const tiposFrete = [
    { valor: "1", label: "CIF" },
    { valor: "2", label: "FOB" },
    { valor: "3", label: "Retirada" }
];

const STATUS_FILTERS = [
    { value: "", label: "Todos" },
    { value: "pendente", label: "Pendente" },
    { value: "respondido", label: "Respondido" },
    { value: "aprovada", label: "Aprovado" },
    { value: "reprovada", label: "Reprovado" },
];

const toNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") return value;

    const cleaned = String(value).replace(/[^\d,.-]/g, "");
    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");
    const decimalSeparator = lastComma > lastDot ? "," : ".";
    const normalized = decimalSeparator === ","
        ? cleaned.replace(/\./g, "").replace(",", ".")
        : cleaned.replace(/,/g, "");

    return Number(normalized) || 0;
};

const formatPercent = (value) => `${Math.abs(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
})}%`;

const getComparativoStatus = (difference) => difference > 0
    ? "acima"
    : difference < 0
        ? "abaixo"
        : "igual";

const getViabilidade = (percentualTotal, diferencaTotal) => {
    if (diferencaTotal <= 0) return "Viável: a resposta reduz ou mantém o custo previsto.";
    if (percentualTotal <= 5) return "Viável com atenção: aumento pequeno sobre o previsto.";
    if (percentualTotal <= 15) return "Requer negociação: aumento relevante sobre o previsto.";
    return "Pouco viável: aumento alto em relação ao que foi pedido.";
};

const getCustoTexto = (difference, percentual) => {
    if (difference > 0) {
        return `Custo total ${Money.formatarValor(Math.abs(difference))} maior (${formatPercent(percentual)} acima).`;
    }

    if (difference < 0) {
        return `Custo total ${Money.formatarValor(Math.abs(difference))} menor (${formatPercent(percentual)} abaixo).`;
    }

    return "Não há diferença de valor.";
};

const getComparativoIcon = (status) => {
    if (status === "acima") return <IoTrendingUpOutline />;
    if (status === "abaixo") return <IoTrendingDownOutline />;
    return <IoInformationOutline />;
};

const normalizarStatus = (status) => String(status || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const obterStatusKey = (status) => {
    const statusNormalizado = normalizarStatus(status);

    if (statusNormalizado.includes("pendente")) return "pendente";
    if (statusNormalizado.includes("respond")) return "respondido";
    if (statusNormalizado.includes("aprov")) return "aprovada";
    if (statusNormalizado.includes("reprov")) return "reprovada";
    if (statusNormalizado.includes("analise")) return "analise";

    return statusNormalizado;
};

const disabledInput = {
    backgroundColor: "#ebebeba1"
}

export const PropostaVisualizacao = () => {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState("detalhes");
    const [expandedProposta, setExpandedProposta] = useState(id);
    const [expandedFornecedor, setExpandedFornecedor] = useState({});
    const [filtroStatus, setFiltroStatus] = useState("");
    const [dadosGeraisResposta, setDadosGeraisResposta] = useState({
        tipoPagamento: "",
        formaPagamento: "",
        tipoFrete: "",
        prazoEntrega: "",
        observacoes: ""
    });
    const [dadosGeraisErrors, setDadosGeraisErrors] = useState({});

    const toggleFornecedorExpansion = (fornecedor) => {
        setExpandedFornecedor((prev) => ({
            ...prev,
            [fornecedor]: !prev[fornecedor],
        }));
    };

    const dataInicio = localStorage.getItem('proposalTableFilters_dataInicio');
    const dataFim = localStorage.getItem('proposalTableFilters_dataFim');

    const enabled = !!user && !!dataInicio && !!dataFim;
    const isComprador = user?.tipusu === "COMPRADOR";

    const result = isComprador
        ? usePropostasComprador(user, id, dataInicio, dataFim, false, 1, 5, enabled && isComprador)
        : usePropostasFornecedor(user, user?.codope, id, dataInicio, dataFim, false, 1, 5, enabled && !isComprador);

    const propostas = result.data;
    const isLoading = result.isLoading;

    const { responderPropostaAsync, isLoading: isLoadingResposta } = useResponderProposta(user);

    const togglePropostaExpansion = useCallback((propostaId) => {
        setExpandedProposta(prev => prev === propostaId ? null : propostaId);
    }, []);

    const handleVoltar = () => {
        navigate(-1);
    };

    const determinarStatus = (proposta) => {
        const status = normalizarStatus(proposta?.status);

        if (status.includes("negocia")) {
            return proposta?.status_res ? obterStatusKey(proposta.status_res) : "pendente";
        }

        return obterStatusKey(proposta?.status);
    };

    const handleDadosGeraisRespostaChange = (e) => {
        const { name, value } = e.target;
        let errorMsg = "";

        setDadosGeraisResposta(prev => ({
            ...prev,
            [name]: value
        }));

        switch (name) {
            case "observacoes":
                if (value.length > 65535) errorMsg = "Observações excede o máximo de caracteres permitidos";
                break;
            case "formaPagamento":
                if (value.length > 150) errorMsg = "Forma de pagamento excede o máximo de caracteres permitidos";
                break;
            case "prazoEntrega":
                if (value) {
                    const [ano, mes, dia] = value.split("-").map(Number);
                    const dataInserida = new Date(ano, mes - 1, dia);
                    const hoje = new Date();
                    hoje.setHours(0, 0, 0, 0);
                    if (dataInserida < hoje) errorMsg = "O prazo de entrega não pode ser uma data passada";
                }
                break;
        }

        setDadosGeraisErrors(prev => ({
            ...prev,
            [name]: errorMsg
        }));
    };

    const getDadosGeraisErrors = (fieldName) => dadosGeraisErrors[fieldName] || "";

    const renderStatus = useCallback((proposta) => {
        const statusKey = determinarStatus(proposta);
        const status = STATUS_MAP[statusKey] || STATUS_MAP.pendente;

        return (
            <span
                className={Style.badge}
                style={{
                    backgroundColor: `${status.color}25`,
                    color: status.color,
                    fontWeight: 'bold'
                }}
            >
                {status.icon && <span className={Style.badgeIcon}>{status.icon}</span>}
                {status.label}
            </span>
        );
    }, []);

    const DetalhesTab = React.memo(({ proposta }) => {
        if (!proposta) {
            return (
                <div className={Style.emptyState}>
                    <IoDocumentTextOutline className={Style.emptyIcon} />
                    <h3>Nenhum detalhe encontrado.</h3>
                </div>
            );
        }

        return (
            <div className={Style.respostaContainer}>
                <div className={Style.resumoProposta}>
                    <div className={Style.respostasHeader}>
                        <h3><IoInformationCircleOutline style={{ marginRight: 8 }} /> Proposta {user.tipusu === "FORNECEDOR" ? "Recebida" : "Enviada"}</h3>
                        <p>Detalhes da proposta {user.tipusu === "FORNECEDOR" ? "recebida" : "enviada pelo comprador"}</p>
                    </div>

                    <div className={Style.resumoItens}>
                        <div className={Style.resumoItem}>
                            <span>Data da Proposta:</span>
                            <strong>{proposta.datpro || "-"}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Código da Proposta:</span>
                            <strong>{proposta.arq281}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Código do Fornecedor:</span>
                            <strong>{proposta.codfor || "-"}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Nome do Fornecedor:</span>
                            <strong>{proposta.nomcli || "-"}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>CPF/CNPJ do Fornecedor:</span>
                            <strong>{proposta.cgccli || "-"}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Código Produto (Fornecedor):</span>
                            <strong>{proposta.profor || "-"}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Código do Produto:</span>
                            <strong>{proposta.codpro || "-"}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Nome do Produto:</span>
                            <strong>{proposta.nompro || "-"}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Tipo do Produto:</span>
                            <strong>{proposta.tippro || "-"}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Descrição do Produto:</span>
                            <strong>{proposta.despro || "-"}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Quantidade Proposta:</span>
                            <strong>{proposta.qtdpro || "0"} {proposta.univen}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Unidade de Venda:</span>
                            <strong>{proposta.univen || "-"}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Valor Unitário Proposto:</span>
                            <strong>{Money.formatarValor(proposta.valpro)}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>CST ICMS:</span>
                            <strong>{proposta.pericm || "-"}%</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>CST IPI:</span>
                            <strong>{proposta.peripi || "-"}%</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Forma de Pagamento:</span>
                            <strong>{`${proposta.forpag} - ${proposta.desforpag}` || "-"}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Tipo de Pagamento:</span>
                            <strong>{`${proposta.tippag} - ${proposta.destippag}`}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Tipo de Frete:</span>
                            <strong>{`${proposta.tipfre} - ${proposta.desfre}`}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Prazo de Entrega:</span>
                            <strong>{proposta.praent || "-"}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Nome do Comprador:</span>
                            <strong>{proposta.nomope || "-"}</strong>
                        </div>

                        <div className={Style.resumoItem}>
                            <span>Telefone do Comprador:</span>
                            {proposta.celcorp ? (
                                <a
                                    href={`https://wa.me/55${proposta.celcorp.replace(/\D/g, "")}`}
                                    target="_blank"
                                    title="Clique para conversar com o comprador no whatsapp"
                                    rel="noopener noreferrer"
                                    style={{ color: "#25D366", textDecoration: "none", fontWeight: "bold" }}
                                >
                                    {proposta.celcorp}
                                    <IoLogoWhatsapp style={{ fontSize: '20px', marginLeft: '6px' }} />
                                </a>
                            ) : (
                                <strong>-</strong>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    });

    const FormResposta = ({ arq281, formData, setFormData, dadosGerais, setDadosGeraisErrors, onSuccess, setErrors, getErrors }) => {
        const handleSubmit = async (e) => {
            e.preventDefault();

            const data = {
                arq281: arq281,
                qtdpro: formData.quantidade,
                valpro: formData.valor,
                tipfre: dadosGerais.tipoFrete,
                praent: dadosGerais.prazoEntrega,
                forpag: dadosGerais.formaPagamento,
                tippag: dadosGerais.tipoPagamento,
                csticm: formData.cstIcm,
                cstipi: formData.cstIpi,
                obspro: dadosGerais.observacoes,
            };

            const dadosValidacao = {
                ...formData,
                ...dadosGerais
            };

            const camposObrigatorios = Validacoes.validarCamposObrigatorios(dadosValidacao, [
                "valor",
                "cstIcm",
                "cstIpi",
                "tipoPagamento",
                "formaPagamento",
                "tipoFrete",
                "prazoEntrega"
            ], {
                valor: "Valor Unitário Proposto (R$)",
                tipoPagamento: "Tipo Pagamento",
                formaPagamento: "Forma Pagamento",
                tipoFrete: "Tipo Frete",
                prazoEntrega: "Prazo Entrega",
                cstIcm: "CST ICMS",
                cstIpi: "CST IPI"
            })

            Object.entries(camposObrigatorios).forEach(([field, msg]) => {
                if (["tipoPagamento", "formaPagamento", "tipoFrete", "prazoEntrega"].includes(field)) {
                    setDadosGeraisErrors(prev => ({
                        ...prev,
                        [field]: msg
                    }));
                } else {
                    setErrors(field, msg);
                }
            });

            const hasValidationErrors = Object.values(camposObrigatorios).some(msg => msg && msg.length > 0);
            if (hasValidationErrors) return;

            try {
                const response = await responderPropostaAsync(data);
                onSuccess();

                await Alert({
                    icon: "success",
                    title: "Sucesso",
                    message: response.docs.mensagem || "Proposta respondida com sucesso!",
                    showConfirmButton: true,
                    confirmButtonText: "OK",
                });
            } catch (error) {
                await Alert({
                    icon: "error",
                    title: "Erro",
                    message: error.response?.data?.erro || "Falha ao enviar resposta",
                    showConfirmButton: true,
                    confirmButtonText: "OK",
                });
            }
        };

        const handleChange = (e) => {
            const { name, value } = e.target;
            let errorMsg = "";

            const novoFormData = { ...formData, [name]: value };
            setFormData(novoFormData);

            switch (name) {
                case "valor":
                    const numericValue = parseFloat(value.replace(/\./g, "").replace(",", "."));
                    if (value.replace(/\D/g, "").length > 13 || numericValue > 1000000000.00)
                        errorMsg = "Valor excede o máximo permitido";
                    break;
                case "cstIcm":
                    if (!Validacoes.cstIcms(value)) errorMsg = "CST ICMS inválido";
                    break;
                case "cstIpi":
                    if (!Validacoes.cstIpi(value)) errorMsg = "CST IPI inválido";
                    break;
                case "observacoes":
                    if (value.length > 65535) errorMsg = "Observações excede o máximo de caracteres permitidos";
                    break;
                case "formaPagamento":
                    if (value.length > 150) errorMsg = "Forma de pagamento excede o máximo de caracteres permitidos";
                    break;
                case "prazoEntrega":
                case "prazoPagamento":
                    const [ano, mes, dia] = value.split("-").map(Number);
                    const dataInserida = new Date(ano, mes - 1, dia);
                    const hoje = new Date();
                    hoje.setHours(0, 0, 0, 0);
                    if (dataInserida < hoje) errorMsg = "O prazo de entrega não pode ser uma data passada";
                    break;
            }

            setErrors(name, errorMsg);
        }


        return (
            <div className={Style.respostaContainer}>
                <div className={Style.respostasHeader}>
                    <h3><IoShare style={{ marginRight: 8 }} /> Enviar Resposta</h3>
                    <p>Preencha os dados específicos deste item.</p>
                </div>

                <form className={Style.respostaForm}>
                    <section className={Style.formSection}>
                        <div className={Style.formSectionHeader}>
                            <div className={Style.dadosItemInfo}>
                                <IoLayersOutline />

                                <div className={Style.dadosItemTexto}>
                                    <h4>Dados do item</h4>
                                    <p>Valores fiscais e comerciais específicos deste produto.</p>
                                </div>
                            </div>
                        </div>

                        <div className={Style.formRow}>
                            <div className={Style.formGroup}>
                                <label htmlFor={`valor-${arq281}`}>Valor Unitário Proposto (R$) *</label>
                                <div className={Style.inputWithIcon}>
                                    <input
                                        id={`valor-${arq281}`}
                                        type="text"
                                        value={formData.valor}
                                        min="1"
                                        maxLength="15"
                                        onChange={(e) => {
                                            let value = Masks.dinheiro(e.target.value);
                                            handleChange({ target: { name: 'valor', value } })
                                        }}
                                        placeholder="R$ 0,00"
                                    />
                                    {getErrors("valor") && <span className="error">{getErrors("valor")}</span>}
                                </div>
                            </div>

                            <div className={Style.formGroup}>
                                <label htmlFor={`quantidade-${arq281}`}>Quantidade</label>
                                <div className={Style.inputWithIcon}>
                                    <input
                                        id={`quantidade-${arq281}`}
                                        type="number"
                                        value={formData.quantidade}
                                        disabled
                                        min="1"
                                        placeholder=""
                                        style={disabledInput}
                                        className={Style.formInput}
                                    />
                                    {getErrors("quantidade") && <span className="error">{getErrors("quantidade")}</span>}
                                </div>
                            </div>
                        </div>

                        <div className={Style.formRow}>
                            <div className={Style.formGroup}>
                                <label htmlFor={`cstIcm-${arq281}`}>CST ICMS (Percentual) *</label>
                                <input
                                    id={`cstIcm-${arq281}`}
                                    type="text"
                                    value={formData.cstIcm}
                                    placeholder="0.0%"
                                    onChange={(e) => {
                                        let value = Masks.cstIcms(e.target.value);
                                        handleChange({ target: { name: 'cstIcm', value } });
                                    }}
                                    maxLength={6}
                                    className={Style.formInput}
                                />
                                {getErrors("cstIcm") && <span className="error">{getErrors("cstIcm")}</span>}
                            </div>

                            <div className={Style.formGroup}>
                                <label htmlFor={`cstIpi-${arq281}`}>CST IPI (Percentual) *</label>
                                <input
                                    id={`cstIpi-${arq281}`}
                                    type="text"
                                    value={formData.cstIpi}
                                    placeholder="0.0%"
                                    onChange={(e) => {
                                        let value = Masks.cstIpi(e.target.value);
                                        handleChange({ target: { name: 'cstIpi', value } });
                                    }}
                                    maxLength={6}
                                    className={Style.formInput}
                                />
                                {getErrors("cstIpi") && <span className="error">{getErrors("cstIpi")}</span>}
                            </div>
                        </div>
                    </section>
                    <div className={Style.formActions}>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className={Style.confirmButton}
                            disabled={isLoadingResposta}
                        >
                            {isLoadingResposta ? (
                                <>
                                    Enviando...
                                </>
                            ) : (
                                <>
                                    <IoSendOutline /> Enviar Resposta
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        );
    };

    const RespostaTab = React.memo(({ arq281 }) => {
        const { data: respostas, isLoading: isLoadingRespostas } = useRespostasFornecedor(user, arq281);

        const respostasArray = Array.isArray(respostas)
            ? respostas
            : respostas
                ? [respostas]
                : [];

        if (isLoadingRespostas) {
            return (
                <div className={Style.tabContent}>
                    <Loading text="Carregando resposta..." />
                </div>
            );
        }

        if (respostasArray.length === 0) {
            return (
                <div className={Style.emptyState}>
                    <IoChatbubbleEllipsesOutline className={Style.emptyIcon} />
                    <h3>Nenhuma resposta encontrada.</h3>
                </div>
            );
        }

        return (
            <div className={Style.respostaContainer}>
                <div className={Style.resumoProposta}>
                    <div className={Style.respostasHeader}>
                        <h3><IoChatbubbleEllipsesOutline style={{ marginRight: 8 }} />{user.tipusu === "FORNECEDOR" ? "Resposta Enviada" : "Resposta Recebida"}</h3>
                        <p>Detalhes da {user.tipusu === "FORNECEDOR" ? "sua resposta" : "resposta do fornecedor"}</p>
                    </div>

                    {respostasArray.map((resposta, index) => (
                        <div key={resposta.id || index} className={Style.resumoItens}>
                            <div className={Style.resumoItem}>
                                <span>Data da Resposta:</span>
                                <strong>
                                    {resposta.datres
                                        ? new Date(resposta.datres).toLocaleString().toString()
                                        : "-"}
                                </strong>
                            </div>

                            <div className={Style.resumoItem}>
                                <span>Código da Resposta:</span>
                                <strong>{resposta.arq491 || "-"}</strong>
                            </div>

                            <div className={Style.resumoItem}>
                                <span>Quantidade:</span>
                                <strong>{resposta.qtdpro || "0"} {resposta.univen}</strong>
                            </div>

                            <div className={Style.resumoItem}>
                                <span>Unidade de Venda:</span>
                                <strong>{resposta.univen || "-"}</strong>
                            </div>

                            <div className={Style.resumoItem}>
                                <span>Valor Unitário Proposto:</span>
                                <strong>{Money.formatarValor(resposta.valpro)}</strong>
                            </div>

                            <div className={Style.resumoItem}>
                                <span>Tipo de Frete:</span>
                                <strong>{`${resposta.tipfre} - ${resposta.desfre}`}</strong>
                            </div>

                            <div className={Style.resumoItem}>
                                <span>Prazo de Entrega:</span>
                                <strong>
                                    {resposta.praent
                                        ? new Date(resposta.praent).toLocaleDateString("pt-BR")
                                        : "-"}
                                </strong>
                            </div>

                            <div className={Style.resumoItem}>
                                <span>Forma de Pagamento:</span>
                                <strong>{resposta.forpag || "-"}</strong>
                            </div>

                            <div className={Style.resumoItem}>
                                <span>Tipo de Pagamento:</span>
                                <strong>{`${resposta.tippag} - ${resposta.destippag}`}</strong>
                            </div>

                            {/* <div className={Style.resumoItem}>
                                <span>Prazo de Pagamento:</span>
                                <strong>
                                    {resposta.prapag
                                        ? new Date(resposta.prapag).toLocaleDateString("pt-BR")
                                        : "-"}
                                </strong>
                            </div> */}

                            <div className={Style.resumoItem}>
                                <span>CST ICMS:</span>
                                <strong>{resposta.csticm || "-"}%</strong>
                            </div>

                            <div className={Style.resumoItem}>
                                <span>CST IPI:</span>
                                <strong>{resposta.cstipi || "-"}%</strong>
                            </div>

                            <div className={Style.resumoItem}>
                                <span>Observações:</span>
                                <strong>{resposta.obsres || "-"}</strong>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    });

    const ComparativoTab = useCallback(({ proposta }) => {
        const { data: respostas, isLoading: isLoadingRespostas } = useRespostasFornecedor(user, proposta.arq281);

        const respostasArray = Array.isArray(respostas)
            ? respostas
            : respostas
                ? [respostas]
                : [];

        if (isLoadingRespostas) {
            return (
                <div className={Style.tabContent}>
                    <Loading text="Carregando comparativo..." />
                </div>
            );
        }

        if (respostasArray.length === 0) {
            return (
                <div className={Style.emptyState}>
                    <IoStatsChartOutline className={Style.emptyIcon} />
                    <h3>Nenhuma resposta encontrada para comparar.</h3>
                </div>
            );
        }

        const resposta = respostasArray[0];
        const valorPedido = toNumber(proposta.valpro);
        const valorResposta = toNumber(resposta.valpro);
        const quantidadePedida = toNumber(proposta.qtdpro);
        const quantidadeResposta = toNumber(resposta.qtdpro);
        const totalPedido = valorPedido * quantidadePedida;
        const totalResposta = valorResposta * quantidadeResposta;
        const diferencaValor = valorResposta - valorPedido;
        const diferencaTotal = totalResposta - totalPedido;
        const percentualValor = valorPedido > 0 ? (diferencaValor / valorPedido) * 100 : 0;
        const percentualTotal = totalPedido > 0 ? (diferencaTotal / totalPedido) * 100 : 0;
        const maiorValor = Math.max(valorPedido, valorResposta, 1);
        const maiorTotal = Math.max(totalPedido, totalResposta, 1);

        const valorStatus = getComparativoStatus(diferencaValor);
        const totalStatus = getComparativoStatus(diferencaTotal);

        const getAnaliseValor = () => {
            if (valorStatus === "acima") {
                return `Valor unitário ${Money.formatarValor(Math.abs(diferencaValor))} maior (${formatPercent(percentualValor)} acima).`;
            }

            if (valorStatus === "abaixo") {
                return `Valor unitário ${Money.formatarValor(Math.abs(diferencaValor))} menor (${formatPercent(percentualValor)} abaixo).`;
            }

            return "Resposta no mesmo valor unitário do pedido.";
        };

        const comparacoes = [
            {
                label: "Valor unitário",
                icon: <IoCashOutline />,
                pedido: Money.formatarValor(valorPedido),
                resposta: Money.formatarValor(valorResposta),
                diferenca: Money.formatarValor(diferencaValor),
                status: valorStatus,
            },
            {
                label: "Quantidade",
                icon: <IoAlbumsOutline />,
                pedido: `${quantidadePedida || 0} ${proposta.univen || ""}`,
                resposta: `${quantidadeResposta || 0} ${resposta.univen || proposta.univen || ""}`,
                diferenca: `${quantidadeResposta - quantidadePedida}`,
                status: getComparativoStatus(quantidadeResposta - quantidadePedida),
            },
            {
                label: "Total estimado",
                icon: <IoStatsChartOutline />,
                pedido: Money.formatarValor(totalPedido),
                resposta: Money.formatarValor(totalResposta),
                diferenca: Money.formatarValor(diferencaTotal),
                status: totalStatus,
            },
        ];

        return (
            <div className={Style.comparativoContainer}>
                <div className={Style.respostasHeader}>
                    <h3><IoStatsChartOutline style={{ marginRight: 8 }} /> Comparativo</h3>
                    <p>Comparação entre o pedido do comprador e a resposta do fornecedor</p>
                </div>

                <div className={Style.comparativoHighlights}>
                    <div className={`${Style.comparativoHighlight} ${Style[valorStatus]}`}>
                        <span><IoCashOutline /> Custo unitário</span>
                        <strong>{Money.formatarValor(diferencaValor)}</strong>
                        <small>{getAnaliseValor()}</small>
                    </div>
                    <div className={`${Style.comparativoHighlight} ${Style[totalStatus]}`}>
                        <span><IoStatsChartOutline /> Custo total</span>
                        <strong>{Money.formatarValor(diferencaTotal)}</strong>
                        <small>{getCustoTexto(diferencaTotal, percentualTotal)}</small>
                    </div>
                    <div className={`${Style.comparativoHighlight} ${Style[totalStatus]}`}>
                        <span>{getComparativoIcon(totalStatus)} Viabilidade</span>
                        <strong>{totalStatus === "acima" ? "Negociar" : "Favorável"}</strong>
                        <small>{getViabilidade(percentualTotal, diferencaTotal)}</small>
                    </div>
                </div>

                <div className={Style.comparativoCharts}>
                    <div className={Style.comparativoChartCard}>
                        <h4>Valor unitário</h4>
                        <div className={Style.barChart}>
                            <div className={Style.barRow}>
                                <span>Pedido</span>
                                <div className={Style.barTrack}>
                                    <div className={Style.barPedido} style={{ width: `${(valorPedido / maiorValor) * 100}%` }} />
                                </div>
                                <strong>{Money.formatarValor(valorPedido)}</strong>
                            </div>
                            <div className={Style.barRow}>
                                <span>Resposta</span>
                                <div className={Style.barTrack}>
                                    <div className={Style.barResposta} style={{ width: `${(valorResposta / maiorValor) * 100}%` }} />
                                </div>
                                <strong>{Money.formatarValor(valorResposta)}</strong>
                            </div>
                        </div>
                    </div>

                    <div className={Style.comparativoChartCard}>
                        <h4>Total estimado</h4>
                        <div className={Style.barChart}>
                            <div className={Style.barRow}>
                                <span>Pedido</span>
                                <div className={Style.barTrack}>
                                    <div className={Style.barPedido} style={{ width: `${(totalPedido / maiorTotal) * 100}%` }} />
                                </div>
                                <strong>{Money.formatarValor(totalPedido)}</strong>
                            </div>
                            <div className={Style.barRow}>
                                <span>Resposta</span>
                                <div className={Style.barTrack}>
                                    <div className={Style.barResposta} style={{ width: `${(totalResposta / maiorTotal) * 100}%` }} />
                                </div>
                                <strong>{Money.formatarValor(totalResposta)}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={Style.comparativoTable}>
                    <div className={`${Style.comparativoRow} ${Style.comparativoRowHeader}`}>
                        <span>Indicador</span>
                        <span>Pedido</span>
                        <span>Resposta</span>
                        <span>Diferença</span>
                    </div>
                    {comparacoes.map((item) => (
                        <div key={item.label} className={Style.comparativoRow}>
                            <span className={Style.comparativoIndicator}>{item.icon}{item.label}</span>
                            <strong>{item.pedido}</strong>
                            <strong>{item.resposta}</strong>
                            <em className={Style[item.status]}>{getComparativoIcon(item.status)}{item.diferenca}</em>
                        </div>
                    ))}
                </div>
            </div>
        );
    }, [user]);

    const PropostaItem = React.memo(({ proposta, index }) => {
        const { setFormData, getFormData, setErrors, getErrors } = useFormRespostaPropostaStore();

        const status = determinarStatus(proposta);

        const statusClass = {
            aprovada: Style.headerAprovado,
            reprovada: Style.headerReprovado,
            pendente: Style.headerPendente,
            respondido: Style.headerRespondido
        }[status] || "";

        const podeResponder = user.tipusu === "FORNECEDOR" && (status === "pendente" || status === "analise");
        const podeVisualizarResposta = user.tipusu !== "FORNECEDOR" || !["pendente", "analise"].includes(status);
        const tabAtual = activeTab === "resposta" && !podeVisualizarResposta ? "detalhes" : activeTab;

        const handleSuccessResposta = () => {
            setActiveTab("detalhes");
        };

        const [formData, setLocalFormData] = useState(() => ({
            valor: "",
            quantidade: proposta.qtdpro,
            prazoPagamento: "",
            cstIcm: proposta.pericm,
            cstIpi: proposta.peripi,
            ...getFormData(proposta.arq281)
        }));

        useEffect(() => {
            setFormData(proposta.arq281, formData);
        }, [formData, proposta.arq281, setFormData]);

        return (
            <div className={Style.propostaItem}>
                <div
                    className={`${Style.propostaHeader} ${statusClass}`}
                    onClick={() => togglePropostaExpansion(proposta.arq281)}
                >
                    <div className={Style.propostaInfo}>
                        <div className={Style.propostaExpandIcon}>
                            {expandedProposta === proposta.arq281 ?
                                <IoChevronDownOutline /> :
                                <IoChevronForwardOutline />
                            }
                        </div>
                        <div className={Style.propostaMainInfo}>
                            <div className={Style.propostaTitleRow}>
                                <h4>{`${proposta.arq281} - ${proposta.nompro}` || "Produto sem nome."}</h4>
                            </div>

                            <div className={Style.propostaMeta}>
                                <span className={Style.propostaMetaItem}>
                                    <IoLayersOutline />
                                    {proposta.profor || "N/A"}
                                </span>
                                <span className={Style.propostaMetaItem}>
                                    <IoCashOutline />
                                    {Money.formatarValor(proposta.valpro)}
                                </span>
                                <span className={Style.propostaMetaItem}>
                                    <IoCalendarOutline />
                                    {proposta.datpro}
                                </span>
                                <span className={Style.propostaMetaItem}>
                                    <IoAlbumsOutline />
                                    {proposta.qtdpro || "0"} {proposta.univen}
                                </span>
                            </div>

                        </div>
                        <div className={Style.propostaStatus}>
                            {renderStatus(proposta)}
                        </div>
                    </div>
                </div>

                {expandedProposta === proposta.arq281 && (
                    <div className={Style.propostaContent}>
                        <div className={Style.propostaTabs}>
                            <button
                                className={`${Style.propostaTab} ${tabAtual === "detalhes" ? Style.activePropostaTab : ""}`}
                                onClick={() => setActiveTab("detalhes")}
                            >
                                <IoInformationCircle style={{ marginRight: 8 }} />
                                Detalhes
                            </button>
                            {podeResponder && (
                                <button
                                    className={`${Style.propostaTab} ${activeTab === "responder" ? Style.activePropostaTab : ""}`}
                                    onClick={() => setActiveTab("responder")}
                                >
                                    <IoShare style={{ marginRight: 8 }} />
                                    Responder
                                </button>
                            )}
                            {podeVisualizarResposta && (
                                <button
                                    className={`${Style.propostaTab} ${activeTab === "resposta" ? Style.activePropostaTab : ""}`}
                                    onClick={() => setActiveTab("resposta")}
                                >
                                    <IoChatbubbleEllipsesOutline style={{ marginRight: 8 }} />
                                    Resposta
                                </button>
                            )}
                            {user.tipusu === "COMPRADOR" && (
                                <button
                                    className={`${Style.propostaTab} ${activeTab === "comparativo" ? Style.activePropostaTab : ""}`}
                                    onClick={() => setActiveTab("comparativo")}
                                >
                                    <IoStatsChartOutline style={{ marginRight: 8 }} />
                                    Comparativo
                                </button>
                            )}
                        </div>

                        <div className={Style.propostaTabContent}>
                            {tabAtual === "detalhes" && (
                                <DetalhesTab
                                    proposta={proposta}
                                />
                            )}
                            {tabAtual === "responder" && podeResponder && (
                                <FormResposta
                                    arq281={proposta.arq281}
                                    formData={formData}
                                    setFormData={setLocalFormData}
                                    dadosGerais={dadosGeraisResposta}
                                    setDadosGeraisErrors={setDadosGeraisErrors}
                                    onSuccess={handleSuccessResposta}
                                    getErrors={getErrors}
                                    setErrors={setErrors}
                                />
                            )}
                            {tabAtual === "resposta" && podeVisualizarResposta && (
                                <RespostaTab
                                    arq281={proposta.arq281}
                                />
                            )}
                            {tabAtual === "comparativo" && user.tipusu === "COMPRADOR" && (
                                <ComparativoTab
                                    proposta={proposta}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    });

    let listaPropostas = [];

    try {
        listaPropostas = propostas || [];
    } catch (error) {
        const message = error.response?.data?.erro || error.message;
        Alert({
            icon: "error",
            title: "Erro",
            message: message || "Falha ao obter propostas",
            showConfirmButton: true,
            confirmButtonText: "OK",
        });
    }

    if (isLoading) {
        return <Loading text="Carregando propostas..." />;
    }

    if (!listaPropostas.length) {
        return (
            <div className={Style.container}>
                <div className={Style.header}>
                    <button className={Style.backButton} onClick={handleVoltar}>
                        <IoArrowBackCircle className={Style.backIcon} />
                    </button>
                    <h2>Nenhuma proposta encontrada</h2>
                </div>
                <div className={Style.emptyState}>
                    <IoDocumentTextOutline className={Style.emptyIcon} />
                    <h3>Sem propostas</h3>
                    <p>Não há propostas disponíveis ou você não tem acesso.</p>
                </div>
            </div>
        );
    }

    const propostasAgrupadas = (propostas) => {
        if (!propostas?.length) return {};

        if (user.tipusu === "COMPRADOR") {
            return propostas.reduce((acc, proposta) => {
                const key = proposta.codfor;

                if (!acc[key]) {
                    acc[key] = {
                        codfor: proposta.codfor,
                        nomcli: proposta.nomcli,
                        propostas: []
                    };
                }

                acc[key].propostas.push(proposta);
                return acc;
            }, {});
        }

        return {};
    };

    const propostasFiltradas = filtroStatus
        ? listaPropostas.filter(p => determinarStatus(p) === filtroStatus)
        : listaPropostas;

    const totalItensParaResponder = user.tipusu === "FORNECEDOR"
        ? propostasFiltradas.filter(p => ["pendente", "analise"].includes(determinarStatus(p))).length
        : 0;

    const temItensParaResponder = totalItensParaResponder > 0;

    const contarStatus = (propostasLista = []) => {
        return propostasLista.reduce((acc, proposta) => {
            const status = determinarStatus(proposta) || "pendente";
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});
    };

    const renderFiltroStatus = () => (
        <div className={Style.filtroPropostas}>
            <span>Status Item:</span>
            <div className={Style.statusFilterGroup}>
                {STATUS_FILTERS.map((filter) => (
                    <button
                        key={filter.value || "todos"}
                        type="button"
                        className={`${Style.statusFilterButton} ${filtroStatus === filter.value ? Style.statusFilterButtonActive : ""}`}
                        onClick={() => setFiltroStatus(filter.value)}
                    >
                        {filter.label}
                    </button>
                ))}
            </div>
        </div>
    );

    const renderDadosGeraisResposta = () => (
        <div className={Style.dadosGeraisResposta}>
            <div className={Style.formSectionHeader}>
                <div className={Style.dadosGeraisInfo}>
                    <IoInformationCircleOutline />

                    <div className={Style.dadosGeraisTexto}>
                        <h4>Dados gerais da resposta</h4>
                        <p>Estas condições serão usadas em todos os itens que você responder.</p>
                    </div>
                </div>
                <span className={Style.dadosGeraisBadge}>
                    {totalItensParaResponder} {totalItensParaResponder === 1 ? "item pendente" : "itens pendentes"}
                </span>
            </div>

            <div className={Style.formGrid}>
                <div className={Style.formGroup}>
                    <label htmlFor="dadosGeraisTipoPagamento">Tipo de Pagamento *</label>
                    <select
                        id="dadosGeraisTipoPagamento"
                        value={dadosGeraisResposta.tipoPagamento}
                        onChange={(e) => {
                            handleDadosGeraisRespostaChange({ target: { name: 'tipoPagamento', value: e.target.value } });
                        }}
                    >
                        <option value="" disabled>Selecione o tipo de pagamento...</option>
                        {tiposPagamento.map(tipo => (
                            <option key={tipo.valor} value={tipo.valor}>
                                {tipo.valor} - {tipo.label}
                            </option>
                        ))}
                    </select>
                    {getDadosGeraisErrors("tipoPagamento") && <span className="error">{getDadosGeraisErrors("tipoPagamento")}</span>}
                </div>

                <div className={Style.formGroup}>
                    <label htmlFor="dadosGeraisFormaPagamento">Forma de Pagamento *</label>
                    <input
                        id="dadosGeraisFormaPagamento"
                        type="text"
                        value={dadosGeraisResposta.formaPagamento}
                        maxLength="150"
                        onChange={(e) => {
                            handleDadosGeraisRespostaChange({ target: { name: 'formaPagamento', value: e.target.value } });
                        }}
                        placeholder="Ex: 30/60/90 dias"
                    />
                    {getDadosGeraisErrors("formaPagamento") && <span className="error">{getDadosGeraisErrors("formaPagamento")}</span>}
                </div>

                <div className={Style.formGroup}>
                    <label htmlFor="dadosGeraisTipoFrete">Tipo de Frete *</label>
                    <select
                        id="dadosGeraisTipoFrete"
                        value={dadosGeraisResposta.tipoFrete}
                        onChange={(e) => {
                            handleDadosGeraisRespostaChange({ target: { name: 'tipoFrete', value: e.target.value } });
                        }}
                    >
                        <option value="" disabled>Selecione o tipo de frete...</option>
                        {tiposFrete.map(frete => (
                            <option key={frete.valor} value={frete.valor}>
                                {frete.valor} - {frete.label}
                            </option>
                        ))}
                    </select>
                    {getDadosGeraisErrors("tipoFrete") && <span className="error">{getDadosGeraisErrors("tipoFrete")}</span>}
                </div>

                <div className={Style.formGroup}>
                    <label htmlFor="dadosGeraisPrazoEntrega">Prazo de Entrega *</label>
                    <input
                        id="dadosGeraisPrazoEntrega"
                        type="date"
                        value={dadosGeraisResposta.prazoEntrega}
                        onChange={(e) => {
                            handleDadosGeraisRespostaChange({ target: { name: 'prazoEntrega', value: e.target.value } });
                        }}
                    />
                    {getDadosGeraisErrors("prazoEntrega") && <span className="error">{getDadosGeraisErrors("prazoEntrega")}</span>}
                </div>
            </div>

            <div className={Style.formGroup}>
                <label htmlFor="dadosGeraisObservacoes">Observações</label>
                <textarea
                    id="dadosGeraisObservacoes"
                    value={dadosGeraisResposta.observacoes}
                    onChange={(e) => {
                        handleDadosGeraisRespostaChange({ target: { name: 'observacoes', value: e.target.value } });
                    }}
                    placeholder="Digite aqui suas observações, condições especiais, etc."
                    rows="4"
                />
                {getDadosGeraisErrors("observacoes") && <span className="error">{getDadosGeraisErrors("observacoes")}</span>}
            </div>
        </div>
    );

    if (!propostasFiltradas.length) {
        return (
            <div className={Style.container}>
                <div className={Style.header}>
                    <button className={Style.backButton} onClick={handleVoltar}>
                        <IoArrowBackCircle className={Style.backIcon} />
                    </button>
                    <h2>Propostas - Ordem {listaPropostas[0]?.ordcom} </h2>
                </div>
                {renderFiltroStatus()}
                <div className={Style.emptyState}>
                    <IoDocumentTextOutline className={Style.emptyIcon} />
                    <h3>
                        Sem propostas
                        {filtroStatus
                            ? ` ${filtroStatus === "pendente"
                                ? "pendentes"
                                : filtroStatus === "respondido"
                                    ? "respondidas"
                                    : filtroStatus === "aprovada"
                                        ? "aprovadas"
                                        : filtroStatus === "reprovada"
                                            ? "reprovadas"
                                            : ""
                            }`
                            : ""}
                    </h3>
                    <p>Nenhuma proposta encontrada para o filtro solicitado.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={Style.container}>
            <div className={Style.header}>
                <button className={Style.backButton} onClick={handleVoltar}>
                    <IoArrowBackCircle className={Style.backIcon} />
                </button>
                <h2>Propostas - Ordem {listaPropostas[0]?.ordcom} </h2>

                {user.tipusu === "COMPRADOR" && (
                    listaPropostas.length === 1 ? (
                        <p className={Style.totalPropostas}>{listaPropostas.length} item</p>
                    ) : (
                        <p className={Style.totalPropostas}>{listaPropostas.length} itens</p>
                    )
                )}
            </div>
            {renderFiltroStatus()}
            {temItensParaResponder && renderDadosGeraisResposta()}
            {
                user.tipusu === "COMPRADOR"
                    ? Object.entries(propostasAgrupadas(propostasFiltradas))
                        .map(([codfor, dados]) => {
                            const statusCounts = contarStatus(dados.propostas);
                            const isExpanded = expandedFornecedor[codfor];

                            return (
                                <div key={codfor} className={Style.grupoFornecedor}>
                                    <div
                                        className={Style.fornecedorHeader}
                                        onClick={() => toggleFornecedorExpansion(codfor)}
                                    >
                                        <div className={`${Style.fornecedorExpandIcon} ${isExpanded ? Style.fornecedorExpandIconActive : ""}`}>
                                            {isExpanded
                                                ? <IoChevronDownOutline />
                                                : <IoChevronForwardOutline />}
                                        </div>

                                        <div className={Style.fornecedorInfo}>
                                            <div className={Style.fornecedorTitleRow}>
                                                <h3>
                                                    <Link
                                                        to={`/fornecedores/${codfor}`}
                                                        className={Style.linkFornecedor}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {codfor} - {dados.nomcli.slice(0, 42)}
                                                    </Link>
                                                </h3>
                                                <span className={Style.fornecedorTotal}>{dados.propostas.length} proposta(s)</span>
                                            </div>

                                            <div className={Style.fornecedorMeta}>
                                                <span>
                                                    <IoCalendarOutline />
                                                    {dados.propostas[0]?.datpro}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className={Style.listaProposta}>
                                            {dados.propostas.map((proposta, index) => (
                                                <PropostaItem key={index} proposta={proposta} index={index} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    : (
                        <div className={Style.listaProposta}>
                            {propostasFiltradas.map((proposta, index) => (
                                <PropostaItem key={index} proposta={proposta} index={index} />
                            ))
                            }
                        </div>
                    )
            }
        </div>
    );
};

